import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  FORMATTING_LOCALE,
  formatCurrency,
  formatDate,
  formatDateLong,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatRelativeTime,
  formatTime,
} from './lib/format.js';
import en from './locales/en/common.json';

/**
 * i18next is wired from the very first commit on purpose: every user-visible string in this
 * app must come from a locale file, so shipping Hindi or Tamil later is a translation job
 * rather than a full-codebase refactor. The UI language is English today.
 *
 * The interpolation formatters below let a translator move a value around inside a sentence —
 * "₹500 received on 11 Aug 2026" reads differently in Tamil — without the component that
 * supplied the number knowing anything about it. Money arrives as integer paise and is
 * formatted by the shared package, so a locale string can never accidentally print a float.
 */
export const DEFAULT_NAMESPACE = 'common';
export const FALLBACK_LANGUAGE = 'en';

export { FORMATTING_LOCALE };

export const resources = {
  en: { common: en },
} as const;

/** `{{amount, currency}}`, `{{value, dateLong}}`, `{{count, number}}` — usable in any string. */
const formatters: Record<string, (value: unknown) => string> = {
  currency: (value) => (typeof value === 'number' ? formatCurrency(value) : String(value ?? '')),
  currencyCompact: (value) =>
    typeof value === 'number' ? formatCurrency(value, { compact: true }) : String(value ?? ''),
  number: (value) => (typeof value === 'number' ? formatNumber(value) : String(value ?? '')),
  percent: (value) => (typeof value === 'number' ? formatPercent(value) : String(value ?? '')),
  date: (value) => formatDate(value as string | number | Date),
  dateLong: (value) => formatDateLong(value as string | number | Date),
  dateTime: (value) => formatDateTime(value as string | number | Date),
  time: (value) => formatTime(value as string | number | Date),
  relative: (value) => formatRelativeTime(value as string | number | Date),
};

void i18next.use(initReactI18next).init({
  resources,
  lng: FALLBACK_LANGUAGE,
  fallbackLng: FALLBACK_LANGUAGE,
  ns: [DEFAULT_NAMESPACE],
  defaultNS: DEFAULT_NAMESPACE,
  interpolation: {
    // React already escapes.
    escapeValue: false,
    format: (value, format) => {
      if (!format) return String(value ?? '');
      const formatter = formatters[format];
      return formatter ? formatter(value) : String(value ?? '');
    },
  },
});

export default i18next;
